import { ValidatorFn, AsyncValidatorFn } from '@angular/forms';
import { ValidatorRegistry } from '../registries/validator-registry';
import { ValidatorConfig, AsyncValidatorConfig } from '../models/validator-config.model';
import * as SyncValidators from './sync-validators';
import * as AsyncValidators from './async-validators';

/**
 * Factory to create validators from schema configuration
 */
export class ValidatorFactory {
  /**
   * Initialize and register all default validators
   */
  static initializeDefaultValidators(): void {
    console.log('[ValidatorFactory] Initializing default validators...');

    // Register sync validators
    ValidatorRegistry.registerSync('required', SyncValidators.requiredValidator);
    ValidatorRegistry.registerSync('email', SyncValidators.emailValidator);
    ValidatorRegistry.registerSync('minLength', SyncValidators.minLengthValidator);
    ValidatorRegistry.registerSync('maxLength', SyncValidators.maxLengthValidator);
    ValidatorRegistry.registerSync('min', SyncValidators.minValidator);
    ValidatorRegistry.registerSync('max', SyncValidators.maxValidator);
    ValidatorRegistry.registerSync('pattern', SyncValidators.patternValidator);
    ValidatorRegistry.registerSync('phone', SyncValidators.phoneValidator);
    ValidatorRegistry.registerSync('url', SyncValidators.urlValidator);
    ValidatorRegistry.registerSync('alphanumeric', SyncValidators.alphanumericValidator);
    ValidatorRegistry.registerSync('numeric', SyncValidators.numericValidator);
    ValidatorRegistry.registerSync('matchField', SyncValidators.matchFieldValidator);
    ValidatorRegistry.registerSync('fileType', SyncValidators.fileTypeValidator);
    ValidatorRegistry.registerSync('fileSize', SyncValidators.fileSizeValidator);

    // Register async validators
    ValidatorRegistry.registerAsync('usernameAvailable', AsyncValidators.usernameAvailableValidator);
    ValidatorRegistry.registerAsync('emailAvailable', AsyncValidators.emailAvailableValidator);
    ValidatorRegistry.registerAsync('apiValidator', AsyncValidators.apiValidator);

    console.log('[ValidatorFactory] Registered sync validators:', ValidatorRegistry.getRegisteredSyncKeys());
    console.log('[ValidatorFactory] Checking email validator:', ValidatorRegistry.getSync('email'));
  }

  /**
   * Build sync validators from config array
   */
  static buildSyncValidators(configs?: ValidatorConfig[]): ValidatorFn[] {
    console.log('[ValidatorFactory] buildSyncValidators called with configs:', configs);

    if (!configs || configs.length === 0) {
      console.log('[ValidatorFactory] No configs provided, returning empty array');
      return [];
    }

    const validators = configs
      .map(config => {
        console.log(`[ValidatorFactory] Processing validator config:`, config);
        const validatorFactory = ValidatorRegistry.getSync(config.key);
        console.log(`[ValidatorFactory] Validator factory for "${config.key}":`, validatorFactory);

        if (!validatorFactory) {
          console.warn(`Sync validator "${config.key}" not found in registry`);
          return null;
        }

        const validator = validatorFactory(config.params);
        console.log(`[ValidatorFactory] Created validator instance for "${config.key}":`, validator);
        console.log(`[ValidatorFactory] Validator type:`, typeof validator);
        console.log(`[ValidatorFactory] Validator is null?:`, validator === null);
        console.log(`[ValidatorFactory] Validator is undefined?:`, validator === undefined);
        return validator;
      })
      .filter((v): v is ValidatorFn => v !== null);

    console.log('[ValidatorFactory] Final validators array:', validators, 'Count:', validators.length);
    return validators;
  }

  /**
   * Build async validators from config array
   */
  static buildAsyncValidators(configs?: AsyncValidatorConfig[]): AsyncValidatorFn[] {
    if (!configs || configs.length === 0) {
      return [];
    }

    return configs
      .map(config => {
        const validatorFactory = ValidatorRegistry.getAsync(config.key);
        if (!validatorFactory) {
          console.warn(`Async validator "${config.key}" not found in registry`);
          return null;
        }
        return validatorFactory({ ...config.params, debounceMs: config.debounceMs });
      })
      .filter((v): v is AsyncValidatorFn => v !== null);
  }

  /**
   * Get error message for a validator config
   */
  static getErrorMessage(config: ValidatorConfig, fieldLabel?: string): string {
    if (config.errorMessage) {
      return config.errorMessage;
    }

    const label = fieldLabel || 'Field';

    // Default error messages
    const defaultMessages: Record<string, string> = {
      required: `${label} is required`,
      email: 'Please enter a valid email address',
      minLength: `Minimum length is ${config.params?.['length'] || config.params?.['minLength'] || 0} characters`,
      maxLength: `Maximum length is ${config.params?.['length'] || config.params?.['maxLength'] || 0} characters`,
      min: `Minimum value is ${config.params?.['min'] || config.params?.['value'] || 0}`,
      max: `Maximum value is ${config.params?.['max'] || config.params?.['value'] || 0}`,
      pattern: `${label} format is invalid`,
      phone: 'Please enter a valid phone number',
      url: 'Please enter a valid URL',
      alphanumeric: `${label} must contain only letters and numbers`,
      numeric: `${label} must contain only numbers`,
      matchField: `${label} does not match`,
      fileType: 'Invalid file type',
      fileSize: 'File size exceeds the limit'
    };

    return defaultMessages[config.key] || `${label} is invalid`;
  }
}

// Initialize default validators on import
ValidatorFactory.initializeDefaultValidators();
